import React from "react";
import type { TypeMenuItemProps } from "../MenuItem";
import type { TypeMenuGroupProps } from "./MenuGroupTypes";
import { StyledMenuGroup, StyledTitle, StyledMenuGroupUl } from "./styles";
import { mapMenuItems } from "../hooks/useMenuChildren";

/**
 * @link https://seeds.sproutsocial.com/components/seeds-react-menu/#menugroup
 *
 * @description MenuGroup is used to group lists of {@link https://seeds.sproutsocial.com/components/seeds-react-menu/#menuitem MenuItems}.
 *
 * @example
 * <ActionMenu>
 *   <MenuContent>
 *     <MenuGroup title="Group 1" id="1">
 *       <MenuItem>Item 1</MenuItem>
 *     </MenuGroup>
 *     <MenuGroup title="Group 2" id="2">
 *       <MenuItem>Item 2</MenuItem>
 *     </MenuGroup>
 *   </MenuContent>
 * </ActionMenu>
 *
 * @see {@link https://seeds.sproutsocial.com/components/seeds-react-menu/#menuitem MenuItem}
 * @see {@link https://seeds.sproutsocial.com/components/seeds-react-menu/#menucontent MenuContent}
 */

const MenuGroup = <I extends TypeMenuItemProps = TypeMenuItemProps>({
  titleAs = "div",
  children: childrenProp,
  title,
  color,
  isSingleSelect,
  id,
  menuItems,
  MenuItemComponent,
  ...rest
}: TypeMenuGroupProps<I>) => {
  // Generate a unique ID for the title
  const titleId = `menu-group-title-${Math.random().toString(36).slice(2, 11)}`;

  const children =
    childrenProp || !menuItems || !menuItems.length
      ? childrenProp
      : mapMenuItems({ menuItems, MenuItemComponent });

  if (!children) {
    return null;
  }
  return (
    // role="none" on the outer <li>: this wrapper is purely visual (it exists
    // for the border-bottom divider between groups in the parent menu <ul>).
    // The ARIA group semantics live on the inner <ul> below — axe rejects
    // role="group" on <li>, and a native <ul> here would trigger the `list`
    // rule because its children are <li role="none"> menuitem wrappers.
    //
    // TODO(DS-4273): drop the list-based DOM entirely (MenuContent/MenuGroup
    // as <div>, no <ul>/<li>, no role="none" wrappers). This is the followup
    // to DS-4271's minimal ARIA patch.
    <StyledMenuGroup
      role="none"
      id={id}
      isSingleSelect={isSingleSelect}
      // TODO: fix this type since `color` should be valid here. TS can't resolve the correct type.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      color={color}
      {...rest}
    >
      {title && (
        <StyledTitle as={titleAs} id={titleId}>
          {title}
        </StyledTitle>
      )}
      <StyledMenuGroupUl
        role="group"
        aria-labelledby={title ? titleId : undefined}
      >
        {children}
      </StyledMenuGroupUl>
    </StyledMenuGroup>
  );
};

MenuGroup.__MENU_PRIMITIVE_TYPE = "MenuGroup";

export { MenuGroup };
